import { MODULES, MODULE_LABELS } from "@clientflow/shared";
import { CfoDefaultsSettings } from "@/components/cfo-defaults";
import { ComplianceRulesSettings } from "@/components/entity-compliance";
import { RulesAndRatesSettings } from "@/components/rule-settings";
import { TimeSettings } from "@/components/time-tracker";
import { PlanningSettingsPanel } from "@/components/planning";
import { AiSettingsPanel } from "@/pages/staff/ai";
import { LeadSettings } from "@/pages/staff/leads";
import { BookingSettings } from "@/pages/staff/meetings";
import { ProposalSettings, ReviewSettings } from "@/components/lifecycle";
import { BillingAdjustersSettings } from "@/components/billing";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ShieldCheck, ShieldAlert } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useSearch } from "wouter";
import { Empty, ListSearch, Loading, PageHeader, Pager, Section, Segmented, useServerPaged } from "@/components/page";
import { NoThreadSelected, ThreadList, ThreadView, type ThreadSummary } from "@/components/panels";
import { RetentionPanel } from "@/components/retention";
import { BackupsPanel } from "@/components/backups";
import { ClientQuickBooks, FirmQuickBooks, IntegrationServices, LeadsAndImport, StaffConnections } from "@/components/integrations";
import { resolveSettingsTab, SETTINGS_GROUPS, SETTINGS_NO_ACCESS, settingsTabAccess, type SettingsTabKey } from "@/lib/settings-tabs";
import { ClientEmails, GmailConnection } from "@/components/client-email";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatBytes, formatDateTime } from "@/lib/format";
import { DataTools, FirmSettings } from "./season";
import { useMembers } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";
import type { Member, Template } from "@/lib/types";
import { TableScroll } from "@/components/table-scroll";

export function InboxPage({ me }: { me: Me }) {
  const [tab, setTab] = useState<"portal" | "email">(new URLSearchParams(window.location.search).get("tab") === "email" || new URLSearchParams(window.location.search).has("gmail") ? "email" : "portal");
  return (
    <>
      <PageHeader title="Messages" description="Secure portal messages with clients, and the email the team exchanges with them." />
      <Segmented
        label="Messages"
        className="mb-4"
        value={tab}
        onChange={setTab}
        options={[
          { value: "portal", label: "Portal messages" },
          { value: "email", label: "Client email" },
        ]}
      />
      {tab === "portal" ? (
        <PortalInbox me={me} />
      ) : (
        <div className="space-y-6">
          <GmailConnection me={me} />
          <ClientEmails me={me} />
        </div>
      )}
    </>
  );
}

function PortalInbox({ me }: { me: Me }) {
  const [q, setQ] = useState("");
  const { offset, size, paged } = useServerPaged(q.trim());
  const params = new URLSearchParams({ offset: String(offset), limit: String(size) });
  if (q.trim()) params.set("q", q.trim());
  const { data, isLoading } = useQuery<{ items: ThreadSummary[]; total: number }>({ queryKey: [`/api/threads?${params}`], refetchInterval: 30_000, placeholderData: (prev) => prev });
  const [selected, setSelected] = useState<string>();
  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <div className="min-w-0 space-y-3">
            <ListSearch value={q} onChange={setQ} placeholder="Search by client or subject" className="min-w-0 sm:w-full" />
            <ThreadList threads={data?.items ?? []} selected={selected} onSelect={setSelected} showClient paged={paged(data?.total ?? 0)} empty={q.trim() ? "No conversations match your search." : "No messages."} />
          </div>
          <div className="min-h-[400px] min-w-0 rounded-lg border border-card-border bg-card p-4">
            {selected ? <ThreadView id={selected} meId={me.user?.id} /> : <NoThreadSelected />}
          </div>
        </div>
      )}
    </>
  );
}

/** Which services a person works in, and whether they see each one's dashboard (on by default). */
function MemberAccess({ member, onDone }: { member: Member; onDone: () => void }) {
  const qc = useQueryClient();
  const [mods, setMods] = useState<string[]>(member.staffModules);
  const [hidden, setHidden] = useState<string[]>(member.hiddenDashboards ?? []);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", `/api/firm/members/${member.id}`, { staffModules: mods, hiddenDashboards: hidden.filter((x) => mods.includes(x)) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/firm/members"] });
      onDone();
    },
  });
  const toggle = (list: string[], m: string, on: boolean) => (on ? [...new Set([...list, m])] : list.filter((x) => x !== m));
  return (
    <Section title={`${member.name}'s access`} description="Services they work in, and whether they see each service's dashboard (its Overview tab). The work stays visible either way. Changes apply on their next click.">
      <ul className="divide-y divide-border rounded-md border border-border">
        {MODULES.map((m) => {
          const works = mods.includes(m);
          return (
            <li key={m} className="grid gap-2 px-4 py-3 sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-6">
              <span className="text-sm font-medium">{MODULE_LABELS[m]}</span>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={works} onChange={(e) => setMods((c) => toggle(c, m, e.target.checked))} />
                Works in this service
              </label>
              <label className={`flex items-center gap-2 text-sm ${works ? "" : "opacity-50"}`}>
                <input type="checkbox" disabled={!works} checked={works && !hidden.includes(m)} onChange={(e) => setHidden((c) => toggle(c, m, !e.target.checked))} />
                Sees the dashboard
              </label>
            </li>
          );
        })}
      </ul>
      {save.error && <p className="mt-2 text-sm text-destructive">{errorMessage(save.error)}</p>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save access"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </Section>
  );
}

function Team({ me }: { me: Me }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Member[]>({ queryKey: ["/api/firm/members"] });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"admin" | "staff" | "readonly">("staff");
  const [mods, setMods] = useState<string[]>([...MODULES]);
  const [link, setLink] = useState<string | null>(null);
  const invite = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ inviteUrl: string | null }>("POST", "/api/firm/members", { email, name, role, staffModules: mods }),
    onSuccess: (r) => {
      setLink(r.inviteUrl);
      setEmail("");
      setName("");
      qc.invalidateQueries({ queryKey: ["/api/firm/members"] });
    },
  });
  // Deactivating someone first asks who takes over their clients, open work and tasks.
  const [leaving, setLeaving] = useState<Member | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [takeover, setTakeover] = useState("");
  const [left, setLeft] = useState<string | null>(null);
  const update = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { id: string; isActive: boolean; reassignTo?: string }) =>
      api.send<{ stillAssigned?: { clients: number; engagements: number; tasks: number } }>("PATCH", `/api/firm/members/${v.id}`, { isActive: v.isActive, ...(v.reassignTo ? { reassignTo: v.reassignTo } : {}) }),
    onSuccess: (r) => {
      const n = r.stillAssigned;
      setLeft(n && n.clients + n.engagements + n.tasks > 0 ? `Still assigned to them: ${n.clients} client${n.clients === 1 ? "" : "s"}, ${n.engagements} open job${n.engagements === 1 ? "" : "s"}, ${n.tasks} open task${n.tasks === 1 ? "" : "s"}. Until reassigned, client emails go to owners and admins.` : null);
      setLeaving(null);
      setTakeover("");
      qc.invalidateQueries({ queryKey: ["/api/firm/members"] });
    },
  });
  const takers = (data ?? []).filter((x) => x.isActive && !x.pending && x.role !== "readonly" && x.id !== leaving?.id);
  const canManage = hasPermission(me, "members.manage");
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-6">
      <TableScroll label="Team table" className="rounded-lg border border-card-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="hidden px-4 py-3 md:table-cell">Services</th>
              <th className="px-4 py-3">Two-step verification</th>
              {canManage && (
                <th className="px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data?.map((m) => (
              <tr key={m.id} className={m.isActive ? "" : "opacity-50"}>
                <td className="px-4 py-3">
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.email}</p>
                </td>
                <td className="px-4 py-3 capitalize">{m.role}</td>
                <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">
                  {m.role === "owner" || m.role === "admin"
                    ? "All, with dashboards"
                    : m.staffModules.map((x) => `${MODULE_LABELS[x as keyof typeof MODULE_LABELS]}${m.hiddenDashboards?.includes(x) ? " (work only)" : ""}`).join(", ") || "None"}
                </td>
                <td className="px-4 py-3">
                  {m.mfaEnabled ? <ShieldCheck className="h-4 w-4 text-emerald-600" aria-label="On" /> : <ShieldAlert className="h-4 w-4 text-amber-600" aria-label="Not enabled" />}
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {m.isActive && (m.role === "staff" || m.role === "readonly") && (
                      <Button size="sm" variant="ghost" aria-expanded={editing === m.id} onClick={() => setEditing(editing === m.id ? null : m.id)}>
                        Access
                      </Button>
                    )}
                    {m.userId !== me.user?.id && m.role !== "owner" && (
                      <Button size="sm" variant="ghost" onClick={() => {
                          if (m.isActive) {
                            setLeaving(m);
                            setLeft(null);
                          } else update.mutate({ id: m.id, isActive: true });
                        }}>
                        {m.isActive ? "Deactivate" : "Reactivate"}
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      {editing && data?.find((x) => x.id === editing) && <MemberAccess key={editing} member={data.find((x) => x.id === editing)!} onDone={() => setEditing(null)} />}
      {leaving && (
        <Section title={`Deactivate ${leaving.name}`}>
          <p className="mb-3 text-sm text-muted-foreground">They lose access right away. Choose who takes over their clients, open work and open tasks.</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select aria-label="Reassign to" value={takeover} onChange={(e) => setTakeover(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm sm:w-64">
              <option value="">Don't reassign (owners and admins get client emails)</option>
              {takers.map((x) => (
                <option key={x.id} value={x.userId}>
                  {x.name}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" disabled={update.isPending} onClick={() => update.mutate({ id: leaving.id, isActive: false, reassignTo: takeover || undefined })}>
                Deactivate
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setLeaving(null)}>
                Cancel
              </Button>
            </div>
          </div>
          {update.error && <p className="mt-2 text-sm text-destructive">{errorMessage(update.error)}</p>}
        </Section>
      )}
      {left && <p className="rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">{left}</p>}
      {canManage && (
        <Section title="Invite a team member">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              invite.mutate();
            }}
          >
            <div className="grid gap-2 sm:grid-cols-3">
              <Input aria-label="Name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Input aria-label="Email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as "staff")} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="readonly">Read-only</option>
              </select>
            </div>
            {role !== "admin" && (
              <div className="flex flex-wrap gap-3 text-sm">
                {MODULES.map((m) => (
                  <label key={m} className="flex items-center gap-2">
                    <input type="checkbox" checked={mods.includes(m)} onChange={(e) => setMods((c) => (e.target.checked ? [...c, m] : c.filter((x) => x !== m)))} />
                    {MODULE_LABELS[m]}
                  </label>
                ))}
              </div>
            )}
            {invite.error && <p className="text-sm text-destructive">{errorMessage(invite.error)}</p>}
            <Button type="submit" disabled={invite.isPending}>
              Invite
            </Button>
          </form>
          {link && (
            <div className="mt-4 rounded-lg border border-accent/60 bg-accent/10 p-3 text-sm">
              <p className="mb-2 font-medium">Send this sign-up link. It is valid for 7 days and is shown only once.</p>
              <div className="flex gap-2">
                <Input readOnly value={link} onFocus={(e) => e.target.select()} />
                <Button type="button" variant="outline" onClick={() => navigator.clipboard.writeText(link)} aria-label="Copy link">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function Workflows() {
  const { data, isLoading } = useQuery<Template[]>({ queryKey: ["/api/templates"] });
  if (isLoading) return <Loading />;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {data?.map((t) => (
        <Section key={t.id} title={t.name}>
          <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">{MODULE_LABELS[t.module]}</p>
          <ol className="space-y-1 text-sm">
            {t.stages.map((s, i) => (
              <li key={s.key}>
                {i + 1}. {s.name} <span className="text-xs text-muted-foreground">— client portal status: “{s.clientLabel}”</span>
              </li>
            ))}
          </ol>
        </Section>
      ))}
    </div>
  );
}

/** "engagement.moved" reads "Engagement moved"; the raw key shows on hover. */
function auditLabel(action: string) {
  const text = action.replace(/[._]/g, " ").replace(/\bqbo\b/gi, "QuickBooks").replace(/\bcfo\b/gi, "CFO").replace(/\bmfa\b/gi, "two-step verification");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type AuditEntry = { id: number; action: string; actorName: string | null; subjectName?: string | null; targetType: string | null; createdAt: string; ip: string | null };

function AuditLog() {
  const members = useMembers();
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { offset, size, paged } = useServerPaged(`${action.trim()}|${actor}|${from}|${to}`);
  const params = new URLSearchParams({ offset: String(offset), limit: String(size) });
  if (action.trim()) params.set("action", action.trim());
  if (actor) params.set("actorUserId", actor);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const { data, isLoading } = useQuery<{ items: AuditEntry[]; total: number }>({ queryKey: [`/api/firm/audit?${params}`], placeholderData: (prev) => prev });
  const filtered = !!(action.trim() || actor || from || to);
  const filters = (
    <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
      <ListSearch value={action} onChange={setAction} placeholder="Filter by action" />
      <select aria-label="Person" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={actor} onChange={(e) => setActor(e.target.value)}>
        <option value="">Everyone</option>
        {[...members]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name}
              {!m.isActive ? " (inactive)" : ""}
            </option>
          ))}
      </select>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        From
        <Input type="date" aria-label="From date" className="h-9 w-40" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        To
        <Input type="date" aria-label="To date" className="h-9 w-40" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
      </label>
      {filtered && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setAction("");
            setActor("");
            setFrom("");
            setTo("");
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
  if (isLoading) return <Loading />;
  if (!data?.items.length)
    return (
      <>
        {filters}
        <Empty>{filtered ? "No activity matches these filters." : "No activity has been recorded."}</Empty>
      </>
    );
  return (
    <>
      {filters}
      <div className="overflow-hidden rounded-lg border border-card-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Date and time</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.items.map((a) => (
              <tr key={a.id}>
                <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">{formatDateTime(a.createdAt)}</td>
                <td className="px-4 py-2">{a.actorName ?? "System"}</td>
                <td className="px-4 py-2" title={a.action}>
                  {auditLabel(a.action)}
                  {a.subjectName && a.subjectName !== a.actorName && <span className="text-muted-foreground"> · {a.subjectName}'s account</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager paged={paged(data.total)} noun="entries" className="px-4 pb-3" />
      </div>
    </>
  );
}

/** Document storage by year, across the clients this person can see. */
function Storage() {
  const { data, isLoading, error } = useQuery<Array<{ year: number; files: number; clients: number; bytes: number }>>({ queryKey: ["/api/firm/storage"] });
  const total = (data ?? []).reduce((a, r) => a + r.bytes, 0);
  const max = Math.max(1, ...(data ?? []).map((r) => r.bytes));
  return (
    <Section title="Document storage by year" description="Every client's documents are filed in a folder for each year: tax documents in the year their return is filed. Deleted files are not counted.">
      {isLoading ? (
        <Loading />
      ) : error ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !data?.length ? (
        <Empty>No documents have been uploaded yet.</Empty>
      ) : (
        <TableScroll label="Document storage by year table">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-4 font-medium">Year</th>
                <th className="py-2 pr-4 text-right font-medium">Clients</th>
                <th className="py-2 pr-4 text-right font-medium">Files</th>
                <th className="py-2 pr-4 text-right font-medium">Storage</th>
                <th className="w-1/3 py-2 font-medium">
                  <span className="sr-only">Usage</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((r) => (
                <tr key={r.year}>
                  <td className="py-2 pr-4 font-medium">{r.year}</td>
                  <td className="tabular py-2 pr-4 text-right">{r.clients}</td>
                  <td className="tabular py-2 pr-4 text-right">{r.files.toLocaleString()}</td>
                  <td className="tabular py-2 pr-4 text-right">{formatBytes(r.bytes)}</td>
                  <td className="py-2" aria-hidden>
                    <div className="h-2 rounded-full bg-secondary">
                      <div className="h-2 rounded-full bg-primary" style={{ width: `${(r.bytes / max) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border font-semibold">
                <td className="py-2 pr-4">Total</td>
                <td />
                <td className="tabular py-2 pr-4 text-right">{data.reduce((a, r) => a + r.files, 0).toLocaleString()}</td>
                <td className="tabular py-2 pr-4 text-right">{formatBytes(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </TableScroll>
      )}
    </Section>
  );
}

/** Firm Settings in five groups, each with its own tabs; `?tab=` still opens any one directly. */
export function SettingsPage({ me }: { me: Me }) {
  const render: Record<SettingsTabKey, () => ReactNode> = {
    team: () => <Team me={me} />,
    time: () => <TimeSettings />,
    workflows: () => <Workflows />,
    planning: () => <PlanningSettingsPanel me={me} />,
    "compliance-rules": () => <ComplianceRulesSettings canEdit={hasPermission(me, "firm.manage")} />,
    cfo: () => <CfoDefaultsSettings canEdit={hasPermission(me, "firm.manage")} />,
    proposals: () => (
      <div className="space-y-6">
        <ProposalSettings canEdit={hasPermission(me, "firm.manage")} />
        <BillingAdjustersSettings canEdit={hasPermission(me, "firm.manage")} />
      </div>
    ),
    firm: () => (
      <Section title="Firm preferences">
        <FirmSettings me={me} />
      </Section>
    ),
    booking: () => <BookingSettings me={me} />,
    leads: () => <LeadSettings />,
    reviews: () => <ReviewSettings canEdit={hasPermission(me, "firm.manage")} me={me} />,
    data: () => <DataTools me={me} />,
    ai: () => <AiSettingsPanel />,
    storage: () => (
      <div className="space-y-6">
        {hasPermission(me, "firm.manage") && <RetentionPanel />}
        <Storage />
      </div>
    ),
    backups: () => <BackupsPanel />,
    rules: () => <RulesAndRatesSettings />,
    audit: () => <AuditLog />,
    integrations: () => <IntegrationServices />,
    "integrations-staff": () => <StaffConnections me={me} />,
    "integrations-clients": () => <ClientQuickBooks />,
    "integrations-leads": () => <LeadsAndImport />,
    "integrations-firm-qbo": () => <FirmQuickBooks />,
  };
  const access = settingsTabAccess(me);
  const visible = SETTINGS_GROUPS.map((g) => ({ key: g.key, label: g.label, tabs: g.tabs.filter((x) => access[x.key]) })).filter((g) => g.tabs.length);
  const [state, setState] = useState(() => resolveSettingsTab(access, new URLSearchParams(window.location.search).get("tab")));
  const tab = state.tab;
  // A link to another tab of this page (e.g. from Integrations to Firm Settings › AI) opens it.
  const search = useSearch();
  useEffect(() => {
    const k = new URLSearchParams(search).get("tab");
    if (k && k !== tab) setState(resolveSettingsTab(access, k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const group = visible.find((g) => g.key === state.groupKey) ?? visible[0]!;
  const open = (key: SettingsTabKey) => {
    setState(resolveSettingsTab(access, key));
    const u = new URL(window.location.href);
    u.searchParams.set("tab", key);
    window.history.replaceState(null, "", u);
  };
  // A tab this person can't open: no tab of the group is selected and the page says so.
  const current = state.denied ? null : (group.tabs.find((x) => x.key === tab) ?? group.tabs[0]!);
  return (
    <>
      <PageHeader title="Firm Settings" description="Your team and their access, how each service runs, how clients reach you, and your data." />
      <Segmented label="Settings group" options={visible.map((g) => ({ value: g.key, label: g.label }))} value={group.key} onChange={(k) => open(visible.find((g) => g.key === k)!.tabs[0]!.key)} className="mb-4" />
      {group.tabs.length > 1 && (
        // The second level reads lighter than the groups above it.
        <div role="tablist" aria-label={group.label} className="mb-5 flex gap-5 overflow-x-auto border-b border-border">
          {group.tabs.map((x) => (
            <button
              key={x.key}
              type="button"
              role="tab"
              aria-selected={x.key === current?.key}
              // On a phone the tabs scroll sideways; the open one is brought into view.
              ref={x.key === current?.key ? (el) => el?.scrollIntoView?.({ block: "nearest", inline: "nearest" }) : undefined}
              onClick={() => open(x.key)}
              className={`-mb-px whitespace-nowrap border-b-2 px-0.5 pb-2 text-sm font-medium transition-colors ${x.key === current?.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {x.label}
            </button>
          ))}
        </div>
      )}
      {current ? (
        <div key={current.key}>{render[current.key]()}</div>
      ) : (
        <div role="status" className="rounded-lg border border-card-border bg-card px-5 py-10 text-center">
          <h2 className="text-base font-semibold">{SETTINGS_NO_ACCESS}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ask a firm owner or administrator if you need it. The tabs above are the ones you can open.</p>
        </div>
      )}
    </>
  );
}
